import { useState, useEffect } from "react";
import { Button } from "@aidkitorg/component-library";
import { usePost } from "../../API";

type InlineTagsProps = {
  conversationItemId: string;
  tags: string[];
  onTagsUpdated?: (tags: string[]) => void;
};

export function InlineTags({ conversationItemId, tags: initialTags, onTagsUpdated }: InlineTagsProps) {
  const addTag = usePost('/coach/conversation/tags/add');
  const removeTag = usePost('/coach/conversation/tags/remove');
  const [tags, setTags] = useState<string[]>(initialTags);
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [newTag, setNewTag] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Update local state when parent passes new tags
  useEffect(() => {
    setTags(initialTags);
  }, [initialTags]);

  async function handleAddTag() {
    if (!newTag.trim()) return;

    setSaving(true);
    setError(null);

    try {
      const resp = await addTag({
        conversationItemId,
        tag: newTag.trim()
      });

      if (resp.alreadyExists) {
        setError('Tag already added.');
      } else if ('error' in resp) {
        setError(typeof resp.error === 'string' ? resp.error : 'Failed to add tag');
      } else {
        const updatedTags = [...tags, newTag.trim()];
        setTags(updatedTags);
        setNewTag("");
        setIsAdding(false);
        onTagsUpdated?.(updatedTags);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to add tag');
    } finally {
      setSaving(false);
    }
  }

  async function handleRemoveTag(tagToRemove: string) {
    setSaving(true);
    setError(null);

    try {
      const resp = await removeTag({
        conversationItemId,
        tag: tagToRemove
      });

      if ('error' in resp) {
        setError(typeof resp.error === 'string' ? resp.error : 'Failed to remove tag');
      } else {
        const updatedTags = tags.filter(t => t !== tagToRemove);
        setTags(updatedTags);
        onTagsUpdated?.(updatedTags);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to remove tag');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-wrap gap-2 items-center py-2">
      {tags.map((tag, index) => (
        <span
          key={index}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
        >
          {tag}
          <button
            onClick={() => handleRemoveTag(tag)}
            disabled={saving}
            className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
            title="Remove tag"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </span>
      ))}

      {isAdding ? (
        <div className="inline-flex items-center gap-1">
          <input
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleAddTag();
              } else if (e.key === 'Escape') {
                setIsAdding(false);
                setNewTag("");
                setError(null);
              }
            }}
            placeholder="Tag name"
            className="px-2 py-1 text-xs border rounded"
            autoFocus
            disabled={saving}
          />
          <Button
            onClick={handleAddTag}
            disabled={saving || !newTag.trim()}
            size="sm"
          >
            Add
          </Button>
          <Button
            onClick={() => {
              setIsAdding(false);
              setNewTag("");
              setError(null);
            }}
            disabled={saving}
            size="sm"
            variant="secondary"
          >
            Cancel
          </Button>
        </div>
      ) : (
        <button
          onClick={() => setIsAdding(true)}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 border border-dashed border-gray-300 hover:border-gray-400 rounded-full transition-colors"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add tag
        </button>
      )}

      {error && (
        <span className="text-xs text-red-600">{error}</span>
      )}
    </div>
  );
}
